import { useState, useRef, useEffect } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Bell, PaperPlaneRight, CheckCircle } from "@phosphor-icons/react";

export default function GuestRequest() {
  const { propertyId } = useParams();
  const [session, setSession] = useState(null);
  const [room, setRoom] = useState("");
  const [lastName, setLastName] = useState("");
  const [err, setErr] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => { scrollRef.current?.scrollTo({ top: 9e9, behavior: "smooth" }); }, [messages]);

  const verify = async () => {
    setErr(""); setVerifying(true);
    try {
      const { data } = await api.post("/soysos/guest/verify", { property_id: propertyId, room_number: room, last_name: lastName });
      setSession(data);
      setMessages([{ role: "ai", text: `Hi ${data.guest_name.split(" ")[0]}! What can we bring to Room ${data.room_number}?` }]);
    } catch (e) { setErr(formatApiErrorDetail(e?.response?.data?.detail) || "Verification failed"); }
    finally { setVerifying(false); }
  };

  const send = async () => {
    if (!input.trim() || busy) return;
    const text = input.trim();
    setInput("");
    setMessages((m) => [...m, { role: "guest", text }]);
    setBusy(true);
    try {
      const { data } = await api.post("/soysos/guest/request", { session_id: session.session_id, message: text });
      setMessages((m) => [...m, { role: "ai", text: data.reply, tickets: data.tickets }]);
    } catch {
      setMessages((m) => [...m, { role: "ai", text: "Sorry, something went wrong. Please try again or call the front desk." }]);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-secondary/40 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-card rounded-2xl border border-border shadow-lg overflow-hidden flex flex-col" style={{ height: "min(680px, 92vh)" }} data-testid="guest-request-app">
        <div className="px-5 py-4 text-white flex items-center gap-3" style={{ background: "#0EA5E9" }}>
          <div className="h-9 w-9 rounded-full bg-white/20 flex items-center justify-center"><Bell size={20} weight="fill" /></div>
          <div><div className="font-heading font-semibold">Guest Services</div><div className="text-[11px] opacity-90">{session ? `Room ${session.room_number}` : "Verify your room to begin"}</div></div>
        </div>

        {!session ? (
          <div className="flex-1 flex flex-col justify-center p-7">
            <h1 className="font-heading text-xl font-semibold">Welcome</h1>
            <p className="text-sm text-muted-foreground mt-1 mb-5">Confirm your details so we can send requests to your room.</p>
            <div className="space-y-3">
              <div><Label className="text-xs">Room number</Label><Input value={room} onChange={(e) => setRoom(e.target.value)} data-testid="gr-room" className="mt-1" placeholder="412" /></div>
              <div><Label className="text-xs">Last name</Label><Input value={lastName} onChange={(e) => setLastName(e.target.value)} data-testid="gr-lastname" className="mt-1" placeholder="Ramirez" onKeyDown={(e) => e.key === "Enter" && verify()} /></div>
            </div>
            {err && <p className="text-[12.5px] text-destructive mt-3" data-testid="gr-error">{err}</p>}
            <Button className="w-full mt-5" style={{ background: "#0EA5E9" }} onClick={verify} disabled={verifying || !room || !lastName} data-testid="gr-verify">{verifying ? "Verifying…" : "Continue"}</Button>
          </div>
        ) : (
          <>
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-secondary/20">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "guest" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[82%] px-3.5 py-2.5 rounded-2xl text-[13px] leading-relaxed ${m.role === "guest" ? "bg-sky-600 text-white rounded-br-md" : "bg-card border border-border rounded-bl-md"}`}>
                    {m.text}
                    {m.tickets?.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {m.tickets.map((t) => (
                          <div key={t.id} className="flex items-center gap-1.5 text-[11.5px] text-success"><CheckCircle size={13} weight="fill" /> {t.quantity > 1 ? `${t.quantity}× ` : ""}{t.request_type}</div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="p-3 border-t border-border flex items-center gap-2">
              <input data-testid="gr-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="e.g. 2 extra towels and a coffee refill" className="flex-1 h-10 px-3 rounded-full border border-border text-sm outline-none focus:ring-2 focus:ring-sky-500/30" />
              <button data-testid="gr-send" onClick={send} disabled={busy || !input.trim()} className="h-10 w-10 rounded-full text-white flex items-center justify-center disabled:opacity-50" style={{ background: "#0EA5E9" }}><PaperPlaneRight size={17} weight="fill" /></button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
